import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RatioOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NTextBox, TextBoxComponent } from '../../common/text-box/text-box.component';
import { defaultImage } from '../../icms.config';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';

export namespace NCmsHeroBanner {
    export const ID = 'hero-banner';
    export interface IData extends NICmsContentComponent.IData {
        images: IImage[];
    }
    export interface IImage extends NTextBox.IData {
        image: boolean;
        ratio: string;
        link: string;
        overlay?: boolean;
        overlayType?: string;
        roundedCorners?: boolean;
        horizontalAlignment?: string;
        verticalAlignment?: string;
    }
}
@ICmsComponent<CmsHeroBannerComponent>({
    id: NCmsHeroBanner.ID,
    name: 'Hero Banner',
    tags: ['Hero', 'Banner', 'Homepage'],
    fields: [
        <NICmsContentField.IRepeaterField>{
            key: 'images',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Images',
            itemName: 'Image',
            minSize: 1,
            maxSize: 2,
            fields: [
                <NICmsContentField.IToggleField>{
                    key: 'overlay',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Overlay',
                    labels: { true: 'Show', false: 'Hide' },
                    default: false
                },
                <NICmsContentField.IChoiceField>{
                    key: 'overlayType',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Overlay type',
                    options: [
                        { key: '_dark-overlay', title: 'Dark' },
                        { key: '_dark-gradient-overlay', title: 'Dark Gradient' },
                        { key: '_light-overlay', title: 'Light' },
                        { key: '_light-gradient-overlay', title: 'Light Gradient' }
                    ],
                    canShow: (data: NCmsHeroBanner.IImage) => data.overlay
                },
                {
                    key: 'roundedCorners',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Rounded Corners',
                    labels: { true: 'Yes', false: 'No' },
                    default: false
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    default: defaultImage,
                    required: true
                },
                {
                    key: 'ratio',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Ratio',
                    options: RatioOptions,
                    required: true,
                    default: 'ratio-5-2'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    description: 'Link will be applied to the whole element',
                    required: false,
                    default: '/'
                },
                {
                    key: 'contentLabel',
                    type: NICmsContentField.EFieldType.label,
                    title: 'Content'
                },
                ...NTextBox.getFields(false, true),
                {
                    key: 'alignmentLabel',
                    type: NICmsContentField.EFieldType.label,
                    title: 'Content alignment'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'horizontalAlignment',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Horizontal',
                    options: [
                        { key: 'start', title: 'Left' },
                        { key: 'center', title: 'Center' },
                        { key: 'end', title: 'Right' }
                    ],
                    description: 'Horizontal alignment of the box',
                    default: 'left'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'verticalAlignment',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Vertical',
                    options: [
                        { key: 'top', title: 'Top' },
                        { key: 'middle', title: 'Center' },
                        { key: 'bottom', title: 'Bottom' }
                    ],
                    description: 'Vertical alignment of the box',
                    default: 'left'
                }
            ]
        },
        NICmsContentComponent.backgroundField
    ]
})
@Component({
    standalone: true,
    imports: [HrefComponent, NgClass, TextBoxComponent, DfModuleImageModule],
    selector: 'cms-hero-banner',
    templateUrl: './cms-hero-banner.html',
    styleUrl: 'cms-hero-banner.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsHeroBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsHeroBanner.IData;

    getPositionClasses(image: NCmsHeroBanner.IImage): string {
        let classes = 'flex';
        if (image.horizontalAlignment) {
            classes += ` flex-justify-${image.horizontalAlignment}`;
        }
        if (image.verticalAlignment) {
            classes += ` flex-${image.verticalAlignment}`;
        }
        return classes;
    }
}
